import { Formik, Form, Field, ErrorMessage } from "formik";
import { useCallback } from "react";
import * as Yup from "yup";
import { KeenIcon, LazyFormikSelect, CustomOption, CustomSingleValue } from "@/components"; // adjust import if needed
import { useContactOptions } from '@/hooks';
/**
 * Reusable Form Modal Component
 * 
 * Props:
 * - open: boolean (controls visibility)
 * - title: string (modal title)
 * - initialValues: object (Formik initial values)
 * - validationSchema: Yup schema (Formik validation)
 * - onSubmit: function (called with form values)
 * - onClose: function (closes the modal)
 * - loading: boolean (loading state for submit)
 * - fields: array of field configs (for dynamic form rendering)
 */
const RequestFileUploadModal = ({
    open,
    title,
    initialValues,
    validationSchema,
    onSubmit,
    onClose,
    loading,
    fields = [],
    showPatientSelection = false, // pass true if you want to send contact in payload.
    forMultipleContacts = false,
    patientFieldLabel = 'Patient',
    patientFieldHint = '',
    patientPlaceholder = '',
}) => {

    if (!open) return null;
    const loadContactOptions = useContactOptions({ defaultPageSize: 20, filterByEmail: forMultipleContacts });
    const loadPatientOptions = useCallback(async (params) => {
        const result = await loadContactOptions(params);
        return {
            ...result,
            options: (result?.options || []).filter(opt => opt.contact_type !== 'Provider'),
        };
    }, [loadContactOptions]);

    return (
        <div className="modal-wrapper modal-animate-fade">
            <div className="modal-body w-4/5 max-w-xl animate-scale">
                <h3 className="capitalize card-title">{title}</h3>
                <button
                    type="button"
                    onClick={onClose}
                    className="modal-close-btn"
                >
                    <KeenIcon icon="cross" />
                </button>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                >
                    {({ values, errors }) => (
                        console.log('showPatientSelection', showPatientSelection, errors),
                        <Form className="mt-3">

                            {showPatientSelection &&
                                <div className='w-full mb-3'>
                                    <label className="mb-1 form-label">{patientFieldLabel} <span className='pl-px text-red-600'>*</span></label>
                                    <LazyFormikSelect
                                        name='contact_id'
                                        isMulti={forMultipleContacts}
                                        placeholder={patientPlaceholder || `Select a ${patientFieldLabel}`}
                                        loadOptions={forMultipleContacts ? loadContactOptions : loadPatientOptions}
                                        pageSize={20}
                                        preloadedOption={initialValues.contact}
                                        components={{
                                            Option: CustomOption,
                                            SingleValue: CustomSingleValue,
                                        }}
                                    />
                                    {patientFieldHint && <div className="mt-1 text-xs text-gray-600">{patientFieldHint}</div>}
                                    <ErrorMessage name="contact_id" component="div" className="mt-1 text-xs text-red-500" />
                                </div>
                            }
                            {fields.map((field) => (
                                <div key={field.name} className="mb-3">
                                    <label className="items-center gap-1 mb-1 form-label">
                                        {field.label} {field.required && <span className="text-red-600">*</span>}
                                        {field.optional && (
                                            <span className="text-[11px]">(Optional)</span>
                                        )}
                                    </label>
                                    {field.type === "textarea" ? (
                                        <Field
                                            as="textarea"
                                            name={field.name}
                                            rows={field.rows || 5}
                                            className="input pt-2 h-[150px]"
                                        />
                                    ) : (
                                        <Field
                                            type={field.type || "text"}
                                            name={field.name}
                                            className="input"
                                            {...(field.inputProps || {})}
                                        />
                                    )}
                                    <ErrorMessage
                                        name={field.name}
                                        component="div"
                                        className="mt-1 text-xs text-red-500"
                                    />

                                    {field.showCounter && (
                                        <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                                            <span>
                                                {values[field.name].length}/{field.max || 500} characters
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <div className="flex items-center justify-end gap-2 mt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn btn-sm btn-primary"
                                >
                                    {loading ? "Please Wait..." : "Submit"}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="btn btn-sm btn-outline btn-danger"
                                >
                                    Cancel
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export { RequestFileUploadModal };
